import { useCallback, useEffect, useState } from "react";
import { getAllPlansAsync } from "../api/PlanApi";
import { Plan } from "../models/SubscriptionModels";
import { Constants } from "../constants/constants";

interface UseAllPlansReturn {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// Platform-owner "manage plans" list — fetches ALL plans (active and inactive)
// via /api/plans, unlike usePlans which only gets public active plans.
export const useAllPlans = (lazy = false): UseAllPlansReturn => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
      const res = await getAllPlansAsync(token);
      if (res.success) {
        setPlans(res.result ?? []);
        setError(null);
      } else {
        setPlans([]);
        setError(res.message || "Failed to load plans");
      }
    } catch (err) {
      console.error(err);
      setPlans([]);
      setError("Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!lazy) load();
  }, [load, lazy]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { plans, loading, error, reload };
};